import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fmt } from "../../utils/fmt";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import {
  StatCard,
  DataTable,
  PageHeader,
  Button,
  Modal,
  FormInput,
  FormSelect,
  FormTextarea,
  StatusBadge,
  PrintButton,
  PrintHeader,
} from "../../components/common";
import toast from "react-hot-toast";

export function MerchantDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/merchant/dashboard").then((r) => setStats(r.data.data));
  }, []);

  // AED figures come from the server, where each transaction and settlement is
  // converted at the rate frozen on it — not at today's rate.
  const aedTodayRate = stats?.aedTodayRate || 0;

  return (
    <div>
      <PageHeader
        title="Merchant Dashboard"
        subtitle={
          aedTodayRate
            ? `Today's AED rate ${aedTodayRate.toFixed(2)} · past entries stay at their own rate`
            : undefined
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <StatCard
          title="Total Commission"
          value={stats?.totalCommissionAmount || 0}
          index={0}
        />
        <StatCard
          title="Total Pay Out"
          value={stats?.totalPayOutAmount || 0}
          index={1}
        />
        <StatCard
          title="Pay Out Transactions"
          value={stats?.totalPayOutTransactions || 0}
          prefix=""
          index={2}
        />
        <StatCard
          title="Pending"
          value={stats?.pendingCount || 0}
          prefix=""
          index={3}
        />
        <StatCard
          title="Pending Amount"
          value={stats?.pendingAmount || 0}
          index={4}
        />
        <StatCard
          title="Total Payment Dena"
          value={stats?.totalPaymentDena || 0}
          index={5}
        />
        <StatCard
          title="Payment Dena (AED)"
          value={stats?.totalPaymentDenaAed || 0}
          prefix="AED "
          index={0}
        />
        <StatCard
          title="Total Settled (AED)"
          value={stats?.totalSettledAed || 0}
          prefix="AED "
          index={1}
        />
        <StatCard
          title="Total Settled (INR)"
          value={stats?.totalSettledInr || 0}
          index={2}
        />
        {stats?.totalSettledUsdt > 0 && (
          <StatCard
            title="Total Settled (USDT)"
            value={stats.totalSettledUsdt}
            prefix="USDT "
            index={3}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-4 mb-4">
        <div className="bg-white rounded-2xl shadow-card p-5 min-w-[220px]">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Payment Limits
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-emerald-600">Available</span>
              <span className="font-semibold">
                ₹{(stats?.availableLimit || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-600">Used</span>
              <span className="font-semibold">
                ₹{(stats?.usedLimit || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Maximum</span>
              <span className="font-semibold">
                ₹{(stats?.maxPaymentLimit || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Transactions (READ ONLY) ───
export function MerchantTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(null);

  const fetch = () => {
    api.get(`/merchant/transactions?page=${page}&limit=10`).then((r) => {
      setTransactions(r.data.data);
      setTotal(r.data.total);
      setLoading(false);
    });
  };
  useEffect(() => {
    fetch();
  }, [page]);

  const handleExport = async () => {
    const r = await api.get("/reports/export/transactions", {
      responseType: "blob",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = "transactions.xlsx";
    a.click();
  };

  return (
    <div>
      <PageHeader title="Transactions List" />
      <DataTable
        columns={[
          { header: "ID", key: "id" },
          {
            header: "Amount",
            render: (r) => `₹${parseFloat(r.amount).toLocaleString()}`,
          },
          { header: "UTR", render: (r) => r.utrNumber || "-" },
          { header: "UPI ID", render: (r) => r.upiId || "-" },
          { header: "Remark", render: (r) => r.notes || "-" },
          {
            header: "Created",
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
          {
            header: "Status",
            render: (r) => <StatusBadge status={r.status} />,
          },
        ]}
        data={transactions}
        total={total}
        page={page}
        onPageChange={setPage}
        loading={loading}
        onExport={handleExport}
        actions={(r) => (
          <div className="flex gap-2">
            <button
              onClick={() => setShowDetail(r)}
              className="text-brand-500 text-sm hover:underline"
            >
              View All
            </button>
            {r.status === "CLEARED" && (
              <button
                onClick={() => {
                  api
                    .get(`/reports/receipt/${r.id}`, { responseType: "blob" })
                    .then((res) =>
                      window.open(
                        URL.createObjectURL(
                          new Blob([res.data], { type: "application/pdf" }),
                        ),
                        "_blank",
                      ),
                    );
                }}
                className="text-emerald-500 text-sm"
              >
                📥
              </button>
            )}
          </div>
        )}
      />
      <Modal
        open={!!showDetail}
        onClose={() => setShowDetail(null)}
        title="Transaction Details"
      >
        {showDetail && (
          <div className="space-y-2 text-sm">
            {[
              ["Amount", `₹${parseFloat(showDetail.amount).toLocaleString()}`],
              ["Type", showDetail.transactionType],
              ["UPI ID", showDetail.upiId || "-"],
              ["UTR", showDetail.utrNumber || "-"],
              ["Remark", showDetail.notes || "-"],
              ["Status", showDetail.status],
              ...(showDetail.status === "REJECTED"
                ? [
                    [
                      "Reject Reason",
                      showDetail.rejectReason || "No reason provided",
                    ],
                  ]
                : []),
              ["Created", new Date(showDetail.createdAt).toLocaleString()],
              [
                "Cleared",
                showDetail.transactionClearTime
                  ? new Date(showDetail.transactionClearTime).toLocaleString()
                  : "-",
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex justify-between py-1.5 border-b border-gray-50"
              >
                <span className="text-gray-500">{k}</span>
                <span
                  className={`font-medium ${k === "Reject Reason" ? "text-red-600" : ""}`}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Sub-Merchants ───
export function MerchantSubmerchants() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreds, setShowCreds] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    username: "",
    password: "",
  });
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  const fetch = () =>
    api.get("/merchant/submerchants").then((r) => setItems(r.data.data));
  useEffect(() => {
    fetch();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/merchant/submerchants", form);
      toast.success("Created!");
      setShowCreate(false);
      setShowCreds(r.data.credentials);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };
  const handleImpersonate = async (userId) => {
    try {
      await impersonate(userId);
      navigate("/submerchant");
    } catch (e) {
      toast.error("Failed.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Sub-Merchants"
        action={
          <Button onClick={() => setShowCreate(true)}>
            Create Sub-Merchant
          </Button>
        }
      />
      <DataTable
        columns={[
          { header: "Name", key: "name" },
          { header: "Username", render: (r) => r.user?.username || "-" },
          {
            header: "Status",
            render: (r) => (
              <StatusBadge status={r.isActive ? "Active" : "Inactive"} />
            ),
          },
        ]}
        data={items}
        total={items.length}
        page={1}
        actions={(r) => (
          <div className="flex gap-1">
            <button
              onClick={() =>
                handleCopy(
                  `Username: ${r.user?.username}\nPassword: ${r.user?.plainPassword || "N/A"}\nLogin: ${window.location.origin}/login`,
                )
              }
              className="p-1.5 rounded-lg hover:bg-gray-100 text-sm"
              title="Copy"
            >
              📋
            </button>
            <button
              onClick={() => handleImpersonate(r.user?.id)}
              className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
              title="Login as"
            >
              🔑
            </button>
          </div>
        )}
      />
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Sub-Merchant"
      >
        <form onSubmit={handleCreate}>
          <FormInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <FormTextarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <FormInput
            label="Username"
            required
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <FormInput
            label="Password"
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <Button type="submit" className="w-full">
            Create
          </Button>
        </form>
      </Modal>
      <Modal
        open={!!showCreds}
        onClose={() => setShowCreds(null)}
        title="Credentials"
      >
        {showCreds && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Username</span>
              <span className="font-mono font-semibold">
                {showCreds.username}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Password</span>
              <span className="font-mono font-semibold">
                {showCreds.password}
              </span>
            </div>
            <Button
              onClick={() =>
                handleCopy(
                  `Username: ${showCreds.username}\nPassword: ${showCreds.password}`,
                )
              }
              className="w-full mt-2"
              variant="outline"
            >
              Copy All
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function MerchantSettlements() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ amount: "", currency: "AED", remark: "" });

  const loadItems = () =>
    api.get("/merchant/settlements").then((r) => setItems(r.data.data));
  const handleConfirm = async (id) => {
    try {
      await api.post(`/merchant/settlements/${id}/confirm`);
      toast.success("Settlement confirmed!");
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error.");
    }
  };
  useEffect(() => {
    loadItems();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/merchant/settlements", form);
      toast.success("Settlement request created!");
      setShowCreate(false);
      setForm({ amount: "", currency: "AED", remark: "" });
      loadItems();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to create settlement.",
      );
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "Pending";
      case "PICKED":
        return "Picked by Collector";
      case "SUBMITTED":
        return "Submitted by Collector";
      case "REJECTED":
        return "Rejected by Collector";
      case "CONFIRMED":
        return "Confirmed";
      default:
        return status;
    }
  };

  const statusStyle = (status) => {
    switch (status) {
      case "SUBMITTED":
        return "bg-emerald-100 text-emerald-700";
      case "PICKED":
        return "bg-blue-100 text-blue-700";
      case "REJECTED":
        return "bg-red-100 text-red-700";
      case "CONFIRMED":
        return "bg-teal-100 text-teal-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  return (
    <div>
      <PageHeader
        title="Settlement Transactions"
        action={
          <Button onClick={() => setShowCreate(true)}>Create Request</Button>
        }
      />
      <DataTable
        columns={[
          {
            header: "Amount",
            render: (r) =>
              `${r.currency} ${parseFloat(r.amount).toLocaleString()}`,
          },
          {
            header: "Rate",
            render: (r) => {
              const rate =
                r.currency === "USDT"
                  ? parseFloat(r.usdtRate || 0)
                  : parseFloat(r.aedRate || 0);
              return rate > 0 ? rate.toFixed(2) : "-";
            },
          },
          {
            header: "Value (INR)",
            render: (r) =>
              r.amountInr ? `₹${fmt(r.amountInr)}` : "-",
          },
          { header: "Collector", render: (r) => r.collector?.name || "-" },
          { header: "Remark", render: (r) => r.remark || "-" },
          {
            header: "Date",
            render: (r) => new Date(r.createdAt).toLocaleString(),
          },
          {
            header: "Status",
            render: (r) => (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle(r.status)}`}
              >
                {statusLabel(r.status)}
              </span>
            ),
          },
        ]}
        data={items}
        total={items.length}
        page={1}
        actions={(r) => (
          <div className="flex gap-1">
            {r.status === "SUBMITTED" && (
              <Button
                onClick={() => handleConfirm(r.id)}
                variant="primary"
                className="h-7 px-2 text-xs bg-teal-600 hover:bg-teal-700"
              >
                Confirm
              </Button>
            )}
          </div>
        )}
      />
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Settlement Request"
      >
        <form onSubmit={handleCreate}>
          <FormSelect
            label="Currency"
            required
            options={[
              { value: "AED", label: "AED" },
              { value: "USDT", label: "USDT" },
            ]}
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          />
          <FormInput
            label={`Amount (${form.currency})`}
            required
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
          <FormInput
            label="Remark"
            value={form.remark}
            onChange={(e) => setForm({ ...form, remark: e.target.value })}
          />
          <Button type="submit" className="w-full">
            Submit Request
          </Button>
        </form>
      </Modal>
    </div>
  );
}

// <input type="datetime-local"> works in the browser's local time and hands back
// a naive string like "2026-07-29T22:35". The server runs in UTC, so that string
// must be turned into a real instant before sending, otherwise a time typed in
// IST comes back 5.5 hours later (next morning).
const toLocalInput = (value) => {
  const d = value ? new Date(value) : new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const nowLocal = () => toLocalInput();
// Naive local string -> absolute ISO instant with timezone applied.
const toInstant = (local) => (local ? new Date(local).toISOString() : undefined);

export function MerchantConfiguration() {
  const [subMerchants, setSubMerchants] = useState([]);
  const [existingRates, setExistingRates] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    subMerchantId: "",
    usdtTodayRate: "",
    aedTodayRate: "",
    effectiveFrom: nowLocal(),
  });
  const [editRate, setEditRate] = useState(null);

  const fetchRates = () => {
    api.get("/merchant/rate-config").then((r) => {
      setSubMerchants(r.data.subMerchants || []);
      setExistingRates(r.data.rates || []);
    });
    api
      .get("/merchant/rate-config/history")
      .then((r) => setHistory(r.data.data || []))
      .catch(() => {});
  };
  useEffect(() => {
    fetchRates();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/merchant/rate-config", {
        ...form,
        effectiveFrom: toInstant(form.effectiveFrom),
      });
      toast.success("New rate saved — earlier entries keep their old rate.");
      setForm({
        subMerchantId: "",
        usdtTodayRate: "",
        aedTodayRate: "",
        effectiveFrom: nowLocal(),
      });
      fetchRates();
    } catch (e) {
      toast.error("Error updating rate.");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/merchant/rate-config/${editRate.id}`, {
        usdtTodayRate: editRate.usdtTodayRate,
        aedTodayRate: editRate.aedTodayRate,
        effectiveFrom: toInstant(editRate.effectiveFrom),
      });
      toast.success("Rate entry updated.");
      setEditRate(null);
      fetchRates();
    } catch (e) {
      toast.error("Error.");
    }
  };

  const getSubMerchantName = (id) =>
    subMerchants.find((s) => s.id === id)?.name || "-";

  return (
    <div>
      <PageHeader
        title="Configuration"
        subtitle="Set AED and USDT rates for sub-merchants"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            Set New Rate
          </h3>
          <form onSubmit={handleUpdate}>
            <FormSelect
              label="Select Sub-Merchant"
              placeholder="All Sub-Merchants"
              options={subMerchants.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              value={form.subMerchantId}
              onChange={(e) =>
                setForm({ ...form, subMerchantId: e.target.value })
              }
            />
            <FormInput
              label="USDT Today Rate"
              required
              type="number"
              step="0.0001"
              value={form.usdtTodayRate}
              onChange={(e) =>
                setForm({ ...form, usdtTodayRate: e.target.value })
              }
              placeholder="e.g. 92"
            />
            <FormInput
              label="AED Today Rate"
              required
              type="number"
              step="0.0001"
              value={form.aedTodayRate}
              onChange={(e) =>
                setForm({ ...form, aedTodayRate: e.target.value })
              }
              placeholder="e.g. 26.05"
            />
            <FormInput
              label="Applies From (date & time)"
              required
              type="datetime-local"
              value={form.effectiveFrom}
              onChange={(e) =>
                setForm({ ...form, effectiveFrom: e.target.value })
              }
            />
            <p className="text-xs text-gray-400 mb-3">
              Only entries cleared after this time use the new rate. Everything
              already cleared keeps the rate it was cleared at.
            </p>
            <Button type="submit" className="w-full">
              Save Rate
            </Button>
          </form>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            Current Rates
          </h3>
          {existingRates.length === 0 ? (
            <p className="text-sm text-gray-400">No rates configured yet.</p>
          ) : (
            <div className="space-y-3">
              {existingRates.map((rate) => (
                <div
                  key={rate.id}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-xl hover:bg-gray-50 transition-mac"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {rate.merchantId && !rate.agentId
                        ? "All Sub-Merchants (Global)"
                        : ""}
                      {rate.agentId
                        ? `Sub-Merchant: ${getSubMerchantName(rate.agentId)}`
                        : ""}
                    </p>
                    <div className="flex gap-4 mt-1 text-xs text-gray-500">
                      <span>
                        AED:{" "}
                        <strong className="text-green-600">
                          {parseFloat(rate.aedTodayRate).toFixed(4)}
                        </strong>
                      </span>
                      <span>
                        USDT:{" "}
                        <strong className="text-blue-600">
                          {parseFloat(rate.usdtTodayRate).toFixed(4)}
                        </strong>
                      </span>
                      <span>
                        From:{" "}
                        {new Date(
                          rate.effectiveFrom || rate.updatedAt,
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setEditRate({
                        ...rate,
                        aedTodayRate: parseFloat(rate.aedTodayRate),
                        usdtTodayRate: parseFloat(rate.usdtTodayRate),
                        effectiveFrom: toLocalInput(
                          rate.effectiveFrom || rate.createdAt,
                        ),
                      })
                    }
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-sm transition-mac"
                  >
                    ✏️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Rate history — every change is kept, nothing is overwritten */}
      <div className="bg-white rounded-2xl shadow-card p-6 mt-6">
        <h3 className="text-base font-semibold text-gray-800 mb-1">
          Rate History
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          Each row is the rate that applied from that date/time onward. Entries
          are always valued at the rate that was live when they cleared.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-semibold text-gray-600">
                  APPLIES FROM
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">
                  SCOPE
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">
                  AED
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">
                  USDT
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                    No rate changes recorded yet.
                  </td>
                </tr>
              ) : (
                history.map((h) => (
                  <tr key={h.id} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 text-gray-700">
                      {new Date(
                        h.effectiveFrom || h.createdAt,
                      ).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {h.agentId
                        ? getSubMerchantName(h.agentId)
                        : "All Sub-Merchants"}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-green-600">
                      {parseFloat(h.aedTodayRate).toFixed(4)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-blue-600">
                      {parseFloat(h.usdtTodayRate).toFixed(4)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={!!editRate}
        onClose={() => setEditRate(null)}
        title="Edit Rate Entry"
      >
        {editRate && (
          <form onSubmit={handleEdit}>
            <p className="text-sm text-gray-500 mb-4">
              {editRate.agentId
                ? `Sub-Merchant: ${getSubMerchantName(editRate.agentId)}`
                : "All Sub-Merchants (Global)"}
            </p>
            <FormInput
              label="AED Today Rate"
              required
              type="number"
              step="0.0001"
              value={editRate.aedTodayRate}
              onChange={(e) =>
                setEditRate({ ...editRate, aedTodayRate: e.target.value })
              }
            />
            <FormInput
              label="USDT Today Rate"
              required
              type="number"
              step="0.0001"
              value={editRate.usdtTodayRate}
              onChange={(e) =>
                setEditRate({ ...editRate, usdtTodayRate: e.target.value })
              }
            />
            <FormInput
              label="Applies From (date & time)"
              required
              type="datetime-local"
              value={editRate.effectiveFrom || nowLocal()}
              onChange={(e) =>
                setEditRate({ ...editRate, effectiveFrom: e.target.value })
              }
            />
            <p className="text-xs text-gray-400 mb-3">
              This corrects this one history entry. Entries already cleared keep
              the rate frozen on them, so past amounts do not move. To change
              the rate going forward, use "Set New Rate" instead.
            </p>
            <Button type="submit" className="w-full">
              Save Changes
            </Button>
          </form>
        )}
      </Modal>
    </div>
  );
}

// ─── Merchant Ledger — uses per-transaction stored aedRate/usdtRate ───
export function MerchantLedger() {
  const [transactions, setTransactions] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    setLoading(true);
    api
      .get("/merchant/ledger", {
        params: selectedDate
          ? { startDate: selectedDate, endDate: selectedDate }
          : {},
      })
      .then((r) => {
        setTransactions(r.data.data || []);
        setSettlements(r.data.settlements || []);
        setOverall(r.data.overall || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedDate]);

  // Group by sub-merchant, carry per-transaction rates
  const groupBySubMerchant = () => {
    const groups = {};
    transactions.forEach((tx) => {
      const name = tx.subMerchant?.name || "Direct";
      if (!groups[name]) {
        groups[name] = {
          name,
          totalINR: 0,
          totalCommission: 0,
          aedRate: parseFloat(tx.aedRate || 0),
          usdtRate: parseFloat(tx.usdtRate || 0),
        };
      }
      groups[name].totalINR += parseFloat(tx.amount);
      groups[name].totalCommission += parseFloat(tx.merchantCommission || 0);
      // Keep the latest rate seen for this group
      if (tx.aedRate) groups[name].aedRate = parseFloat(tx.aedRate);
      if (tx.usdtRate) groups[name].usdtRate = parseFloat(tx.usdtRate);
    });
    return groups;
  };

  const groups = groupBySubMerchant();

  // Grand totals
  const grandINR = Object.values(groups).reduce((s, g) => s + g.totalINR, 0);
  const grandCommission = Object.values(groups).reduce(
    (s, g) => s + g.totalCommission,
    0,
  );
  const grandBalance = grandINR - grandCommission;

  // Weighted average rates for grand total row
  const txWithRate = transactions.filter((t) => t.aedRate);
  const grandAedRate =
    txWithRate.length > 0
      ? txWithRate.reduce((s, t) => s + parseFloat(t.aedRate), 0) /
        txWithRate.length
      : 0;
  const txWithUsdtRate = transactions.filter((t) => t.usdtRate);
  const grandUsdtRate =
    txWithUsdtRate.length > 0
      ? txWithUsdtRate.reduce((s, t) => s + parseFloat(t.usdtRate), 0) /
        txWithUsdtRate.length
      : 0;

  const grandBalanceAed = grandAedRate > 0 ? grandBalance / grandAedRate : 0;
  const grandBalanceUsdt = grandUsdtRate > 0 ? grandBalance / grandUsdtRate : 0;

  // Settlements already paid for this period, at the rate frozen on each one.
  const settledAed = settlements
    .filter((s) => s.currency !== "USDT")
    .reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const settledUsdt = settlements
    .filter((s) => s.currency === "USDT")
    .reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const settledInr = settlements.reduce(
    (s, r) => s + parseFloat(r.amountInr || 0),
    0,
  );
  const netDena = grandBalance - settledInr;
  const netDenaAed = grandBalanceAed - settledAed;

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  return (
    <div className="printable">
      <PrintHeader title="Merchant Ledger" subtitle={`Date: ${selectedDate || "All dates"}`} />
      <div className="no-print flex items-center justify-between mb-4">
        <PageHeader title="Merchant Ledger" />
        <div className="flex items-end gap-2">
          <PrintButton />
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand-500 transition-mac"
            />
          </div>
          {selectedDate && (
            <button
              onClick={() => setSelectedDate("")}
              className="h-9 px-3 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              All Dates
            </button>
          )}
        </div>
      </div>

      {/* Overall position across every date — matches the dashboard */}
      {overall && (
        <div
          className={`mb-4 rounded-xl border px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm ${
            Math.round(overall.outstandingInr) <= 0
              ? "bg-green-50 border-green-200"
              : "bg-amber-50 border-amber-200"
          }`}
        >
          <span className="font-semibold text-gray-700">
            Overall (all dates):
          </span>
          <span className="text-gray-500">
            Cleared{" "}
            <strong className="text-gray-800">
              ₹{fmt(overall.clearedInr)}
            </strong>
          </span>
          <span className="text-gray-500">
            Commission{" "}
            <strong className="text-red-600">
              ₹{fmt(overall.commissionInr)}
            </strong>
          </span>
          <span className="text-gray-500">
            Settled{" "}
            <strong className="text-orange-600">
              ₹{fmt(overall.settledInr)}
            </strong>{" "}
            <span className="text-xs text-gray-400">
              (AED {fmt(overall.settledAed)}
              {overall.settledUsdt > 0
                ? ` | USDT ${fmt(overall.settledUsdt)}`
                : ""}
              )
            </span>
          </span>
          <span className="text-gray-500">
            {Math.round(overall.outstandingInr) <= 0
              ? "Fully settled"
              : "Still Dena"}{" "}
            <strong
              className={
                Math.round(overall.outstandingInr) <= 0
                  ? "text-green-700"
                  : "text-amber-700"
              }
            >
              ₹{fmt(overall.outstandingInr)}
            </strong>{" "}
            <span className="text-xs text-gray-400">
              (AED {fmt(overall.outstandingAed)})
            </span>
          </span>
        </div>
      )}

      <div className="bg-brand-500 text-white text-center py-3 rounded-t-xl font-bold text-lg">
        MERCHANT DAILY COLLECTION (
        {selectedDate
          ? new Date(selectedDate)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
              .replace(/\//g, "-")
          : "ALL DATES"}
        )
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-b-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-50">
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-12">
                SR.
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                SUB MERCHANT
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                RATE
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                TOTAL AMOUNT (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                COMMISSION (INR)
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                DENA BALANCE
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                SETTLED
              </th>
              <th className="border border-gray-300 px-3 py-2 text-right font-semibold text-gray-700">
                NET DENA
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groups).length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="border border-gray-200 px-3 py-6 text-center text-gray-400"
                >
                  No cleared transactions for this date.
                </td>
              </tr>
            ) : (
              Object.values(groups).map((group, idx) => {
                const balance = group.totalINR - group.totalCommission;
                const balanceAed =
                  group.aedRate > 0 ? balance / group.aedRate : 0;
                const balanceUsdt =
                  group.usdtRate > 0 ? balance / group.usdtRate : 0;
                return (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="border border-gray-200 px-3 py-2 text-center text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 font-semibold text-gray-800">
                      {group.name.toUpperCase()}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right text-gray-500 text-xs">
                      {group.aedRate > 0 ? group.aedRate.toFixed(2) : "-"}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right font-medium">
                      ₹{fmt(group.totalINR)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right text-red-600 font-medium">
                      ₹{fmt(group.totalCommission)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right">
                      <div className="font-semibold text-green-600">
                        ₹{fmt(balance)}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        AED {fmt(balanceAed)} | USDT {fmt(balanceUsdt)}
                      </div>
                    </td>
                    {idx === 0 && (
                      <>
                        <td
                          className="border border-gray-200 px-3 py-2 text-right text-orange-600 font-medium align-top"
                          rowSpan={Object.keys(groups).length}
                        >
                          {settledAed > 0 && <div>AED {fmt(settledAed)}</div>}
                          {settledUsdt > 0 && (
                            <div>USDT {fmt(settledUsdt)}</div>
                          )}
                          {settledAed === 0 && settledUsdt === 0 && (
                            <span className="text-gray-300">—</span>
                          )}
                          {settledInr > 0 && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              ₹{fmt(settledInr)}
                            </div>
                          )}
                        </td>
                        <td
                          className="border border-gray-200 px-3 py-2 text-right align-top"
                          rowSpan={Object.keys(groups).length}
                        >
                          <div
                            className={`font-semibold ${
                              Math.round(netDena) <= 0
                                ? "text-green-600"
                                : "text-blue-600"
                            }`}
                          >
                            ₹{fmt(netDena)}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            AED {fmt(netDenaAed)}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
            <tr className="bg-brand-500 text-white font-bold">
              <td
                className="border border-gray-300 px-3 py-2 text-center"
                colSpan={2}
              >
                TOTAL
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {grandAedRate > 0 ? grandAedRate.toFixed(2) : "-"}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandINR)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                ₹{fmt(grandCommission)}
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                <div>₹{fmt(grandBalance)}</div>
                <div className="text-xs font-normal mt-0.5 opacity-90">
                  AED {fmt(grandBalanceAed)} | USDT {fmt(grandBalanceUsdt)}
                </div>
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                {settledAed > 0 && <div>AED {fmt(settledAed)}</div>}
                {settledUsdt > 0 && <div>USDT {fmt(settledUsdt)}</div>}
                <div className="text-xs font-normal mt-0.5 opacity-90">
                  ₹{fmt(settledInr)}
                </div>
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right">
                <div>₹{fmt(netDena)}</div>
                <div className="text-xs font-normal mt-0.5 opacity-90">
                  AED {fmt(netDenaAed)}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Total INR:</span>
          <span className="ml-2 font-bold text-gray-800">₹{fmt(grandINR)}</span>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Commission:</span>
          <span className="ml-2 font-bold text-red-700">
            ₹{fmt(grandCommission)}
          </span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Dena Balance:</span>
          <span className="ml-2 font-bold text-green-600">
            ₹{fmt(grandBalance)}
          </span>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Settled:</span>
          {settledAed > 0 && (
            <span className="ml-2 font-bold text-orange-600">
              AED {fmt(settledAed)}
            </span>
          )}
          {settledUsdt > 0 && (
            <span className="ml-2 font-bold text-orange-600">
              USDT {fmt(settledUsdt)}
            </span>
          )}
          {settledAed === 0 && settledUsdt === 0 && (
            <span className="ml-2 font-bold text-gray-400">—</span>
          )}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">Net Dena:</span>
          <span
            className={`ml-2 font-bold ${
              Math.round(netDena) <= 0 ? "text-green-700" : "text-blue-700"
            }`}
          >
            ₹{fmt(netDena)}
          </span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">AED Rate:</span>
          <span className="ml-2 font-bold text-gray-800">
            {grandAedRate > 0 ? grandAedRate.toFixed(2) : "-"}
          </span>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">In AED:</span>
          <span className="ml-2 font-bold text-green-700">
            {fmt(grandBalanceAed)}
          </span>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <span className="text-gray-500">In USDT:</span>
          <span className="ml-2 font-bold text-purple-700">
            {fmt(grandBalanceUsdt)}
          </span>
        </div>
      </div>
    </div>
  );
}
